import React, {
  Dispatch,
  SetStateAction,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, Row } from 'antd';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import * as _ from 'lodash';

import { ReactComponent as LocationIcon } from 'app/assets/img/icon_location_sunworld.svg';
import { ReactComponent as CalendarIcon } from 'app/assets/img/icon_datepicker.svg';
import { ReactComponent as FilterIcon } from 'app/assets/img/filter-icon.svg';
import { ReactComponent as EmptyIcon } from 'app/assets/img/icon_empty_data.svg';
import { ReactComponent as ArrowDownIcon } from 'app/assets/img/arrow_down.svg';

import RenderIf from 'app/components/RenderIf';
import {
  StyledInputFilter,
  StyledPopover,
  StyledButton,
  CustomPopoverContent,
  LIST_OBJECT,
  LIST_TYPE,
  StyledSelect,
  // LIST_TICKET,
} from 'app/pages/Catalog';
import {
  DateRangePickerContainer,
  ParkContainer,
  ShowContainer,
  StyledTabs,
  Text,
  Title,
} from './styled';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import DatePickerInput from 'app/pages/Catalog/DatePickerInput';
import Header from 'app/pages/Catalog/components/Header';
import useWindowSize from 'app/hooks/useWindowSize';
import StyledButtonComponent from 'app/components/StyledButton';
import useFetchData from 'app/hooks/useFetchData';
import { getShowNewApi } from 'app/services/showApi';
import TicketSkeleton from 'app/pages/Catalog/TicketSkeleton';
import ShowItem from '../ShowItem';
import { PaymentMethod } from 'app/pages/Cart/hooks/useCartNew';
import DialogWarning from 'app/pages/Catalog/components/DialogWarning';
import StyledPagination from 'app/components/StyledPagination';
import { EDIT_ORDER_STEPS } from '../..';
import { I_TicketData } from '../../types';
import { generateUUID } from 'utils/helper';
import ListPark from 'app/pages/Catalog/Mobile/listPark';
import { CustomCheckBox } from 'app/pages/Catalog/components/helper';

interface AddTicketProps {
  preparingData: {
    parks: Record<string, any>[];
    catalogs: Record<string, any>[];
  };
  paymentMethod: PaymentMethod;
  setCurrentStep: Dispatch<SetStateAction<EDIT_ORDER_STEPS>>;
  setTicketAdded: Dispatch<
    SetStateAction<Record<string, Partial<I_TicketData>>>
  >;
  ticketAdded: Record<string, Partial<I_TicketData>>;
  onAcceptAddingTicket: () => void;
  onCancelAddingTicket: () => void;
  setCountDownId: Dispatch<SetStateAction<string>>;
  isAddingNewTicket: boolean;
  bpAllocatedDebt: number | null;
  partner?: any;
}

interface Filter {
  valueType: number[];
  objectType: number[];
  ticketType: number;
  park: number[];
  dateFrom: Date;
  dateTo: Date;
  catalog: string;
  page: number;
  limit: number;
}

const AddTicket: React.FC<AddTicketProps> = props => {
  const {
    preparingData: { parks, catalogs },
    paymentMethod,
    setTicketAdded,
    ticketAdded,
    onAcceptAddingTicket,
    onCancelAddingTicket,
    setCountDownId,
    isAddingNewTicket,
    bpAllocatedDebt,
    partner,
  } = props;

  const { locale } = useGlobalFunction();
  const translations = useSelector(selectTranslations);
  const { device } = useWindowSize();
  const isDesktop = device === 'desktop';

  const isAutoFetchShowData = useRef(true);

  const fetchShowDataRef = useRef<NodeJS.Timeout | null>(null);

  const [warningMsg, setWarningMsg] = useState<string | null>(null);

  const [showFilter, setShowFilter] = useState<Filter>({
    valueType: [],
    objectType: [],
    ticketType: 0,
    park: [],
    dateFrom: new Date(),
    dateTo: new Date(moment().add(30, 'days').format('YYYY-MM-DD')),
    catalog: 'All',
    page: 1,
    limit: 10,
  });

  const userInfo = useSelector(selectInfoUser);

  const isShowTag = useMemo(() => {
    if (!!partner) {
      return !!partner?.bpLongTerm && !!partner?.allowBuyShortTerm;
    }

    return (
      !!userInfo?.businessPartner?.bpLongTerm &&
      !!userInfo?.businessPartner?.allowBuyShortTerm
    );
  }, [userInfo, partner]);

  const onChangeFilter = (
    filter: Partial<Filter>,
    isGetShowNow?: boolean,
    delay = 500,
  ) => {
    const newFilter = {
      ...showFilter,
      ...filter,
    };
    setCountDownId(generateUUID());
    setShowFilter(newFilter);

    if (!isGetShowNow) return;

    if (fetchShowDataRef.current) clearTimeout(fetchShowDataRef.current);

    fetchShowDataRef.current = setTimeout(() => {
      setShowDataList([]);
      onFetchShow({
        payloadData: {
          dateFrom: moment(newFilter.dateFrom).format('YYYY-MM-DD'),
          dateTo: moment(newFilter.dateTo).format('YYYY-MM-DD'),
          park: newFilter.park.toString(),
          areaType: newFilter.valueType.toString(),
          ageType: newFilter.objectType.toString(),
          ...(!!isShowTag && {
            ticketType: String(newFilter.ticketType),
          }),
          catalog: newFilter.catalog,
          page: newFilter.page,
          limit: newFilter.limit,
          // bpAllocatedDebt,
        },
      });
    }, delay);
  };

  const valueOfTypeFilter = useMemo(() => {
    const objectTypeSelected = LIST_OBJECT.filter(item =>
      showFilter.objectType.includes(item.value),
    );

    const valueTypeSelected = LIST_TYPE.filter(item =>
      showFilter.valueType.includes(item.value),
    );

    return [...objectTypeSelected, ...valueTypeSelected]
      .map(item => translations?.[item.label] ?? '')
      .toString();
  }, [
    showFilter.objectType,
    showFilter.valueType,
    translations,
    showFilter.ticketType,
  ]);

  const [showDataList, setShowDataList] = useState<Record<string, any>[]>([]);
  const [showPaginator, setShowPaginator] = useState<Record<string, any>>();
  const { onFetchData: onFetchShow, isLoading: isLoadingShowData } =
    useFetchData({
      actionApi: getShowNewApi,
      payloadData: {
        ...showFilter,
        dateFrom: moment(showFilter.dateFrom).format('YYYY-MM-DD'),
        dateTo: moment(showFilter.dateTo).format('YYYY-MM-DD'),
        park: showFilter.park.toString(),
        // bpAllocatedDebt,
      },
      IIFE: isAutoFetchShowData.current,
      onBefore: () => {
        isAutoFetchShowData.current = false;
      },
      onSuccess: response => {
        setShowDataList(response.data);
        setShowPaginator(response.paginator);
      },
    });

  const catalogOptions = [
    { id: 'All', name: translations?.['b2b.text.all'] ?? '' },
    ...catalogs,
  ];

  const isDisableConfirmBtn = _.isEmpty(ticketAdded);

  return (
    <>
      <Row gutter={[46, 16]}>
        <Col span={isDesktop ? 6 : 24}>
          <ParkContainer
            gutter={isDesktop ? [32, 32] : [0, 0]}
            style={{
              flexDirection: 'column',
              padding: isDesktop ? '36px 25px' : '16px',
            }}
          >
            <RenderIf
              condition={isDesktop}
              fallback={
                <ListPark
                  parkId={showFilter.park}
                  listParkCustom={parks}
                  onChangePark={checkedValues =>
                    onChangeFilter({ park: checkedValues, page: 1 }, true)
                  }
                  translations={translations}
                  triggerBtnCSS={{ marginBottom: 0 }}
                />
              }
            >
              <>
                <Col style={{ display: 'flex', alignItems: 'center' }}>
                  <LocationIcon />
                  <Title style={{ marginLeft: 6 }}>
                    {translations?.['b2b.text.sunworld-park'] ?? ''}
                  </Title>
                </Col>
                <RenderIf condition={!!parks?.length}>
                  <Col>
                    {CustomCheckBox(parks, showFilter.park, checkedValues =>
                      onChangeFilter({ park: checkedValues, page: 1 }, true),
                    )}
                  </Col>
                </RenderIf>
              </>
            </RenderIf>
          </ParkContainer>
        </Col>
        <Col span={isDesktop ? 18 : 24}>
          <ShowContainer style={{ padding: isDesktop ? 32 : 16 }}>
            <Title style={{ fontSize: 16 }}>
              {translations?.['b2b.txt.addNewTicket'] ?? ''}
            </Title>
            <Row
              gutter={isDesktop ? [17, 17] : [8, 8]}
              style={{
                marginTop: isDesktop ? 32 : 16,
                justifyContent: isDesktop ? 'flex-start' : 'space-between',
              }}
              align="bottom"
            >
              <RenderIf condition={!isDesktop}>
                <Col>
                  <StyledSelect
                    options={catalogOptions.map(item => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    onChange={(key: any) => {
                      onChangeFilter({ catalog: key, page: 1 }, true, 0);
                    }}
                    value={showFilter.catalog}
                    suffixIcon={<ArrowDownIcon />}
                  />
                </Col>
              </RenderIf>
              <Col>
                <Row gutter={isDesktop ? [17, 17] : [8, 8]}>
                  <Col>
                    <RenderIf condition={isDesktop}>
                      <Text style={{ marginBottom: 8 }}>
                        {translations?.['b2b.txt.dateFrom-dateTo'] ?? ''}
                      </Text>
                    </RenderIf>
                    <DateRangePickerContainer>
                      <DatePicker
                        dateFormat="EEE (dd/MM/yyyy)"
                        minDate={new Date()}
                        locale={locale}
                        popperPlacement="top"
                        customInput={
                          isDesktop ? (
                            <DatePickerInput
                              calendarIconCSS={{
                                pathCSS: {
                                  stroke: '#000000',
                                },
                              }}
                              containerCSS={{ width: 390 }}
                            />
                          ) : (
                            <CalendarIcon style={{ flexShrink: 0 }} />
                          )
                        }
                        withPortal
                        selectsRange
                        startDate={showFilter.dateFrom}
                        endDate={showFilter.dateTo}
                        onChange={value => {
                          const [start, end] = value as Date[];
                          onChangeFilter(
                            { dateFrom: start, dateTo: end, page: 1 },
                            !!start && !!end,
                            0,
                          );
                        }}
                        shouldCloseOnSelect={true}
                        renderCustomHeader={props => <Header {...props} />}
                        tabIndex={-1}
                      />
                    </DateRangePickerContainer>
                  </Col>
                  <Col>
                    <RenderIf condition={isDesktop}>
                      <Text style={{ marginBottom: 8 }}>
                        {translations?.['b2c.txt.filter'] ?? ''}
                      </Text>
                    </RenderIf>
                    <StyledPopover
                      placement="bottomLeft"
                      content={CustomPopoverContent(
                        showFilter.valueType,
                        checkedValues =>
                          onChangeFilter(
                            { valueType: checkedValues, page: 1 },
                            true,
                            0,
                          ),
                        showFilter.objectType,
                        checkedValues =>
                          onChangeFilter(
                            { objectType: checkedValues, page: 1 },
                            true,
                            0,
                          ),
                      )}
                      trigger="click"
                      overlayInnerStyle={{
                        borderRadius: '20px',
                      }}
                    >
                      <StyledButton style={{ margin: 0 }}>
                        <StyledInputFilter
                          value={valueOfTypeFilter}
                          readOnly
                          placeholder={
                            translations ? translations['b2c.txt.filter'] : ''
                          }
                        />
                        <FilterIcon style={{ flexShrink: 0 }} />
                      </StyledButton>
                    </StyledPopover>
                  </Col>
                </Row>
              </Col>
            </Row>
            <div style={{ marginTop: 36 }}>
              <StyledTabs
                onChange={key => {
                  onChangeFilter({ catalog: key, page: 1 }, true, 0);
                }}
                defaultActiveKey={showFilter.catalog}
                destroyInactiveTabPane
              >
                {catalogOptions?.map(item => {
                  return (
                    <StyledTabs.TabPane key={item.id} tab={item.name}>
                      <RenderIf
                        condition={!isLoadingShowData}
                        fallback={<TicketSkeleton />}
                      >
                        <RenderIf
                          condition={!!showDataList?.length}
                          fallback={
                            <>
                              <div className="d-flex justify-content-center mt-4">
                                <EmptyIcon />
                              </div>
                              <div
                                className="d-flex justify-content-center mt-2"
                                style={{
                                  marginBottom: 30,
                                }}
                              >
                                {translations
                                  ? translations['b2b.txt.MSG001']
                                  : ''}
                              </div>
                            </>
                          }
                        >
                          <>
                            <Row>
                              {showDataList?.map(show => (
                                <Col span={24}>
                                  <ShowItem
                                    setShowDataList={setShowDataList}
                                    key={show?.id}
                                    showData={show}
                                    startDate={showFilter.dateFrom}
                                    endDate={showFilter.dateTo}
                                    paymentMethod={paymentMethod}
                                    setWarningMsg={setWarningMsg}
                                    setTicketAdded={setTicketAdded}
                                    ticketAdded={ticketAdded}
                                    setCountDownId={setCountDownId}
                                    bpAllocatedDebt={bpAllocatedDebt}
                                    valueTicket={showFilter?.ticketType}
                                  />
                                </Col>
                              ))}
                            </Row>
                            <StyledPagination
                              showSizeChanger={false}
                              defaultCurrent={1}
                              current={showFilter.page}
                              total={showPaginator?.totalCount}
                              pageSize={showFilter.limit}
                              onChange={page => {
                                window.scrollTo({
                                  top: 0,
                                  left: 0,
                                  behavior: 'smooth',
                                });
                                onChangeFilter(
                                  { page },
                                  true,
                                  showDataList?.length >= 5 ? 500 : 300,
                                );
                              }}
                            />
                          </>
                        </RenderIf>
                      </RenderIf>
                    </StyledTabs.TabPane>
                  );
                })}
              </StyledTabs>
            </div>
          </ShowContainer>
          <Row style={{ justifyContent: 'center', marginTop: 40 }} gutter={20}>
            <Col>
              <StyledButtonComponent
                textColor="#808080"
                border="solid 1px #808080"
                bgColor="#fff"
                style={{ fontSize: 16 }}
                onClick={onCancelAddingTicket}
              >
                {translations?.['b2b.txt.back']}
              </StyledButtonComponent>
            </Col>
            <Col>
              <StyledButtonComponent
                textColor="#fff"
                style={{ fontSize: 16 }}
                disabled={isDisableConfirmBtn}
                border={isDisableConfirmBtn ? '1px solid #d9d9d9' : undefined}
                onClick={onAcceptAddingTicket}
              >
                {isAddingNewTicket
                  ? 'Loading...'
                  : translations?.['b2b.btn.confirm']}
              </StyledButtonComponent>
            </Col>
          </Row>
        </Col>
      </Row>
      <DialogWarning
        open={!!warningMsg}
        onClose={() => setWarningMsg(null)}
        message={warningMsg ?? ''}
      />
    </>
  );
};

export default AddTicket;
